import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Menu, X, User, Heart, Search, Package, ChevronDown,
  BookOpen, GraduationCap, Briefcase, Scale, Cpu, Baby, ScrollText,
  Sparkles, BookMarked, Church, Palette, School, ClipboardCheck, Stethoscope, Wrench,
  KeyRound,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import CartSheet from "@/components/layout/CartSheet";
import Footer from "@/components/layout/Footer";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const CATEGORIES = [
  { name: "Academic",          icon: GraduationCap, slug: "academic" },
  { name: "Business",          icon: Briefcase,     slug: "business" },
  { name: "Engineering",       icon: Wrench,        slug: "engineering" },
  { name: "Medical",           icon: Stethoscope,   slug: "medical" },
  { name: "Law",               icon: Scale,         slug: "law" },
  { name: "Computer Science",  icon: Cpu,           slug: "computer-science" },
  { name: "Children",          icon: Baby,          slug: "children" },
  { name: "History",           icon: ScrollText,    slug: "history" },
  { name: "Self Help",         icon: Sparkles,      slug: "self-help" },
  { name: "Fiction",           icon: BookMarked,    slug: "fiction" },
  { name: "Religion",          icon: Church,        slug: "religion" },
  { name: "Arts",              icon: Palette,       slug: "arts" },
  { name: "Education",         icon: School,        slug: "education" },
  { name: "Exam Preparation",  icon: ClipboardCheck,slug: "exam-preparation" },
];

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0" aria-label="Blessings Connection — home">
      <img
        src="/logo-blessings.png"
        alt="Blessings Connection"
        width="40"
        height="40"
        className="w-9 h-9 md:w-10 md:h-10 rounded-lg object-contain bg-white"
        loading="eager"
        decoding="async"
      />
      <div className="hidden sm:block leading-tight">
        <div className="font-heading font-semibold text-lg md:text-xl text-secondary tracking-tight">Blessings</div>
        <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary -mt-0.5">Connection</div>
      </div>
    </Link>
  );
}

function HeaderSearch({ onSubmit, testIdPrefix = "", placeholder = "Search by ISBN, title, author, publisher, or keyword" }) {
  const [q, setQ] = useState("");
  const [suggest, setSuggest] = useState(null); // { books, authors, categories, publishers, series }
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  const submit = (e) => { e.preventDefault(); onSubmit(q.trim()); setOpen(false); };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 2) { setSuggest(null); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { default: api } = await import("@/lib/api");
        const { data } = await api.get("/search/autocomplete", { params: { q: q.trim() } });
        setSuggest(data);
        setOpen(true);
      } catch (_) { /* ignore */ }
      finally { setLoading(false); }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const anyResults = suggest && (
    (suggest.books || []).length + (suggest.authors || []).length +
    (suggest.categories || []).length + (suggest.publishers || []).length +
    (suggest.series || []).length > 0
  );

  return (
    <div ref={wrapRef} className="flex-1 max-w-2xl w-full relative">
      <form onSubmit={submit}>
        <div className="flex items-center bg-white border-2 border-slate-200 rounded-xl overflow-hidden focus-within:border-primary focus-within:shadow-ring transition-all">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => suggest && setOpen(true)}
            placeholder={placeholder}
            className="flex-1 min-w-0 h-11 px-4 text-sm bg-transparent outline-none placeholder:text-slate-400"
            data-testid={`${testIdPrefix}header-search-input`}
            aria-label="Search books"
            autoComplete="off"
          />
          <button
            type="submit"
            className="h-11 px-4 md:px-6 bg-primary hover:bg-secondary text-white font-medium text-sm transition-colors flex items-center gap-2"
            data-testid={`${testIdPrefix}header-search-submit`}
          >
            <Search size={16} />
            <span className="hidden md:inline">Search</span>
          </button>
        </div>
      </form>

      {open && suggest && anyResults && (
        <div className="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lift z-[70] max-h-[70vh] overflow-y-auto py-2"
             data-testid={`${testIdPrefix}search-autocomplete`}>
          {suggest.books?.length > 0 && (
            <div className="px-2 pb-2">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Books</p>
              {suggest.books.map((b) => (
                <Link key={b.id} to={b.slug ? `/product/${b.slug}` : `/book/${b.id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`ac-book-${b.id}`}
                  className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50">
                  {b.cover_image && <img src={b.cover_image} alt="" className="w-8 h-11 object-cover rounded" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 truncate font-medium">{b.title}</p>
                    <p className="text-xs text-slate-500 truncate">by {b.author}</p>
                  </div>
                  {b.price && <span className="text-xs font-medium text-primary">${b.price.toFixed(2)}</span>}
                </Link>
              ))}
            </div>
          )}
          {suggest.categories?.length > 0 && (
            <div className="px-2 pb-2 border-t border-slate-100 pt-2">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Categories</p>
              {suggest.categories.map((c) => (
                <Link key={c.id} to={`/books/${(c.path || [c.slug]).join("/")}`}
                  onClick={() => setOpen(false)}
                  data-testid={`ac-cat-${c.slug}`}
                  className="block px-2 py-1.5 rounded-lg hover:bg-slate-50 text-sm text-slate-700 hover:text-primary">
                  <span className="text-slate-400 text-xs">{(c.path || []).slice(0, -1).join(" › ")}{c.level > 0 ? " › " : ""}</span>{c.name}
                </Link>
              ))}
            </div>
          )}
          {suggest.authors?.length > 0 && (
            <div className="px-2 pb-2 border-t border-slate-100 pt-2">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Authors</p>
              {suggest.authors.map((a) => (
                <Link key={a.name} to={`/books?author=${encodeURIComponent(a.name)}`}
                  onClick={() => setOpen(false)}
                  data-testid={`ac-author-${a.name}`}
                  className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-slate-50 text-sm text-slate-700 hover:text-primary">
                  {a.name} <span className="text-xs text-slate-400">({a.count})</span>
                </Link>
              ))}
            </div>
          )}
          {suggest.publishers?.length > 0 && (
            <div className="px-2 pb-2 border-t border-slate-100 pt-2">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Publishers</p>
              {suggest.publishers.map((p) => (
                <Link key={p.name} to={`/books?publisher=${encodeURIComponent(p.name)}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-slate-50 text-sm text-slate-700 hover:text-primary">
                  {p.name} <span className="text-xs text-slate-400">({p.count})</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { totals, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [apiCategories, setApiCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const megaRef = useRef(null);
  const accountRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch live categories once
  useEffect(() => {
    import("@/lib/api").then(({ default: api }) => {
      api.get("/books/categories").then((r) => setApiCategories(r.data || [])).catch(() => {});
      api.get("/categories/tree").then((r) => setCategoryTree(r.data || [])).catch(() => {});
    });
  }, []);

  // ---- Site-wide JSON-LD: Organization + WebSite (mount once) ----
  useEffect(() => {
    const SITE = (process.env.REACT_APP_SITE_URL || "https://blessingsconnection.com").replace(/\/$/, "");
    const inject = (id, data) => {
      let el = document.head.querySelector(`script[type="application/ld+json"][data-seo="${id}"]`);
      if (!el) {
        el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-seo", id);
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };
    inject("org", {
      "@context": "https://schema.org",
      "@type": "OnlineStore",
      name: "Blessings Connection",
      legalName: "Blessings Connection LLC",
      url: SITE,
      logo: `${SITE}/logo-blessings.png`,
      email: "customerservice@blessingsconnection.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "5830 E 2nd St, Ste 7000 #24510",
        addressLocality: "Casper",
        addressRegion: "WY",
        postalCode: "82609",
        addressCountry: "US",
      },
      contactPoint: [{
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "customerservice@blessingsconnection.com",
        availableLanguage: ["en"],
      }],
    });
    inject("website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Blessings Connection",
      url: SITE,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE}/books?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
  }, []);

  // Format book counts (1,243) with US comma grouping
  const fmtCount = (n) => (Number(n) || 0).toLocaleString("en-US");

  // Only show top-level categories with at least one book (either directly or in
  // descendants), sorted by book count desc. Also filter empty children.
  const visibleCategoryTree = useMemo(() => {
    return (categoryTree || [])
      .map((top) => {
        const kids = (top.children || []).filter((c) => (c.book_count || 0) > 0);
        // Sum descendant counts so a parent that only owns descendants still shows.
        const totalCount = (top.book_count || 0) + kids.reduce((s, c) => s + (c.book_count || 0), 0);
        return { ...top, children: kids, total_count: totalCount };
      })
      .filter((top) => (top.total_count || 0) > 0)
      .sort((a, b) => (b.total_count || 0) - (a.total_count || 0));
  }, [categoryTree]);

  // Close mega on route change
  useEffect(() => { setMegaOpen(false); setAccountOpen(false); setMobileOpen(false); }, [location.pathname]);

  // Close menus on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setMegaOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submitSearch = (query) => {
    if (!query) return;
    navigate(`/books?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="App relative min-h-screen bg-[#F8FAFC]">
      {/* Announcement bar */}
      <div className="bg-secondary text-white text-xs md:text-[13px] py-2 text-center px-4" data-testid="announcement-bar">
        Authentic new books · Easy 15-day returns
      </div>

      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-soft"
      >
        {/* Row 1: logo + search + icons */}
        <div className="container-book flex items-center gap-4 md:gap-6 h-16 md:h-20">
          <Logo />

          {/* Search - takes remaining space */}
          <div className="hidden md:flex flex-1">
            <HeaderSearch onSubmit={submitSearch} />
          </div>

          {/* Right icons */}
          <div className="flex items-center gap-1 md:gap-2 ml-auto md:ml-0">
            {/* Order Tracking */}
            <Link
              to="/account"
              className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="nav-track"
            >
              <Package size={18} />
              <span className="hidden xl:inline">Track order</span>
            </Link>

            {/* Wishlist */}
            <Link
              to="/account?tab=wishlist"
              className="hidden md:flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Wishlist"
              data-testid="nav-wishlist"
            >
              <Heart size={20} />
            </Link>

            {/* Account */}
            <div ref={accountRef} className="relative">
              {user ? (
                <>
                  <button
                    onClick={() => setAccountOpen((v) => !v)}
                    className="flex items-center gap-1.5 px-2 md:px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                    data-testid="nav-user-name"
                  >
                    <User size={18} />
                    <span className="hidden md:inline max-w-[100px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
                    <ChevronDown size={14} className="hidden md:inline" />
                  </button>
                  <AnimatePresence>
                    {accountOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        transition={{ duration: 0.14 }}
                        className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lift overflow-hidden"
                        data-testid="account-dropdown"
                      >
                        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                          <div className="text-sm font-medium text-slate-900 truncate">{user.name || user.email}</div>
                          <div className="text-xs text-slate-500 truncate">{user.email}</div>
                        </div>
                        <Link to="/account" className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="account-menu-account">My account</Link>
                        <Link to="/account" className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">Orders & Tracking</Link>
                        <button
                          onClick={() => { setAccountOpen(false); setPwOpen(true); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                          data-testid="nav-change-password"
                        >
                          <KeyRound size={13} className="inline mr-2 -mt-0.5" /> Change password
                        </button>
                        {user.role === "admin" && (
                          <Link to="/admin" className="block px-4 py-2.5 text-sm text-primary font-medium hover:bg-slate-50" data-testid="nav-admin">
                            Admin dashboard
                          </Link>
                        )}
                        <button
                          onClick={() => { setAccountOpen(false); logout(); navigate("/"); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                          data-testid="nav-logout"
                        >
                          Sign out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="nav-login"
                >
                  <User size={18} />
                  <span className="hidden md:inline">Sign in</span>
                </Link>
              )}
            </div>

            {/* Cart */}
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-cart"
              className="relative flex items-center gap-2 px-2 md:px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label={`Cart with ${totals.count} items`}
            >
              <div className="relative">
                <ShoppingBag size={20} />
                {totals.count > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                    {totals.count}
                  </span>
                )}
              </div>
              <span className="hidden md:inline text-sm font-medium">Cart</span>
            </button>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden ml-1 p-2 rounded-lg text-slate-700 hover:bg-slate-100"
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Row 1.5 — mobile search */}
        <div className="md:hidden container-book pb-3">
          <HeaderSearch onSubmit={submitSearch} testIdPrefix="mobile-" placeholder="Search by ISBN, title or author" />
        </div>

        {/* Row 2: categories nav */}
        <div className="hidden md:block border-t border-slate-100 bg-white">
          <div className="container-book flex items-center gap-2 h-11 overflow-visible">
            <div ref={megaRef} className="relative">
              <button
                onClick={() => setMegaOpen((v) => !v)}
                className={`flex items-center gap-2 px-3 h-11 text-sm font-medium transition-colors ${megaOpen ? "text-primary" : "text-slate-700"} hover:text-primary`}
                data-testid="nav-mega-toggle"
              >
                <Menu size={16} /> All Categories
                <ChevronDown size={14} className={`transition-transform ${megaOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {megaOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.14 }}
                    className="absolute left-0 mt-0 w-[calc(100vw-2rem)] max-w-[1100px] bg-white border border-slate-200 rounded-xl shadow-lift p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-6 z-50 max-h-[80vh] overflow-y-auto"
                    data-testid="mega-menu"
                  >
                    {visibleCategoryTree.length > 0 ? (
                      visibleCategoryTree.slice(0, 20).map((top) => (
                        <div key={top.id} data-testid={`mega-col-${top.slug}`} className="min-w-0">
                          <Link
                            to={`/books/${top.slug}`}
                            className="block text-xs font-semibold uppercase tracking-wider text-primary mb-3 hover:text-secondary"
                            data-testid={`mega-cat-${top.slug}`}
                          >
                            {top.name} <span className="text-slate-400 font-normal normal-case">({fmtCount(top.total_count)})</span>
                          </Link>
                          <ul className="space-y-1.5">
                            {(top.children || []).slice(0, 6).map((child) => (
                              <li key={child.id}>
                                <Link
                                  to={`/books/${top.slug}/${child.slug}`}
                                  className="text-sm text-slate-700 hover:text-primary transition-colors block truncate"
                                  data-testid={`mega-sub-${child.slug}`}
                                >
                                  {child.name} <span className="text-slate-400">({fmtCount(child.book_count)})</span>
                                </Link>
                              </li>
                            ))}
                            {(top.children || []).length > 6 && (
                              <li>
                                <Link
                                  to={`/books/${top.slug}`}
                                  className="text-xs text-slate-500 hover:text-primary italic"
                                >See all →</Link>
                              </li>
                            )}
                          </ul>
                        </div>
                      ))
                    ) : (
                      ((apiCategories && apiCategories.length ? apiCategories.map((name) => {
                        const match = CATEGORIES.find((c) => c.name.toLowerCase() === name.toLowerCase());
                        return match || { name, slug: name, icon: BookOpen };
                      }) : CATEGORIES)).slice(0, 14).map((c) => {
                        const Icon = c.icon || BookOpen;
                        return (
                          <Link
                            key={c.name}
                            to={`/shop?category=${encodeURIComponent(c.name)}`}
                            className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
                            data-testid={`mega-cat-${(c.slug || c.name).toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-primary/10 text-slate-700 group-hover:text-primary flex items-center justify-center transition-colors">
                              <Icon size={18} />
                            </div>
                            <span className="text-sm font-medium text-slate-900 group-hover:text-primary">{c.name}</span>
                          </Link>
                        );
                      })
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link to="/books" data-testid="nav-shop" className="px-3 h-11 flex items-center text-sm font-medium text-slate-700 hover:text-primary transition-colors">Shop all</Link>
            <Link to="/books?featured=1" className="px-3 h-11 flex items-center text-sm font-medium text-slate-700 hover:text-primary transition-colors">Featured</Link>
            <Link to="/books/computers-and-technology" className="px-3 h-11 flex items-center text-sm font-medium text-slate-700 hover:text-primary transition-colors">Tech</Link>
            <Link to="/books/test-preparation" className="px-3 h-11 flex items-center text-sm font-medium text-slate-700 hover:text-primary transition-colors">Test Prep</Link>
            <Link to="/about" className="px-3 h-11 flex items-center text-sm font-medium text-slate-700 hover:text-primary transition-colors">About</Link>
            <Link to="/shipping" className="px-3 h-11 flex items-center text-sm font-medium text-slate-700 hover:text-primary transition-colors">Shipping</Link>
            <Link to="/returns" className="px-3 h-11 flex items-center text-sm font-medium text-slate-700 hover:text-primary transition-colors">Returns</Link>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.22 }}
            className="fixed inset-y-0 right-0 w-[85%] max-w-sm bg-white z-[60] shadow-lift overflow-y-auto md:hidden"
            data-testid="mobile-menu"
          >
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <Logo />
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Close menu" data-testid="mobile-menu-close"><X size={20} /></button>
            </div>
            <nav className="p-4">
              <Link to="/books" className="block px-3 py-3 rounded-lg text-slate-900 hover:bg-slate-50 font-medium">Shop all</Link>
              <Link to="/books?featured=1" className="block px-3 py-3 rounded-lg text-slate-900 hover:bg-slate-50 font-medium">Featured</Link>
              <div className="mt-2 mb-1 px-3 text-xs font-semibold uppercase text-slate-400 tracking-wider">Categories</div>
              {visibleCategoryTree.length > 0 ? (
                visibleCategoryTree.slice(0, 20).map((top) => (
                  <Link key={top.id}
                    to={`/books/${top.slug}`}
                    data-testid={`nav-mobile-cat-${top.slug}`}
                    className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50">
                    <span className="text-sm text-slate-900">{top.name}</span>
                    <span className="text-xs text-slate-400">({fmtCount(top.total_count)})</span>
                  </Link>
                ))
              ) : (
                ((apiCategories && apiCategories.length ? apiCategories.map((name) => {
                  const m = CATEGORIES.find((c) => c.name.toLowerCase() === name.toLowerCase());
                  return m || { name, slug: name, icon: BookOpen };
                }) : CATEGORIES)).slice(0, 14).map((c) => {
                  const Icon = c.icon || BookOpen;
                  return (
                    <Link key={c.name} to={`/shop?category=${encodeURIComponent(c.name)}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50">
                      <Icon size={16} className="text-slate-500" />
                      <span className="text-sm">{c.name}</span>
                    </Link>
                  );
                })
              )}
              <div className="mt-2 mb-1 px-3 text-xs font-semibold uppercase text-slate-400 tracking-wider">Account</div>
              {user ? (
                <>
                  <Link to="/account" className="block px-3 py-2.5 text-sm hover:bg-slate-50 rounded-lg" data-testid="nav-mobile-account">My account</Link>
                  {user.role === "admin" && <Link to="/admin" className="block px-3 py-2.5 text-sm text-primary font-medium hover:bg-slate-50 rounded-lg">Admin</Link>}
                  <button onClick={() => { logout(); navigate("/"); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 rounded-lg" data-testid="nav-mobile-logout">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block px-3 py-2.5 text-sm hover:bg-slate-50 rounded-lg">Sign in</Link>
                  <Link to="/register" className="block px-3 py-2.5 text-sm hover:bg-slate-50 rounded-lg">Register</Link>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="min-h-[60vh]">{children}</main>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <CartSheet />
      <Footer />
    </div>
  );
}
